import { useMemo } from 'react'
import { DollarSign, BarChart3, Coins, Landmark, AlertTriangle, Users, Building2 } from 'lucide-react'
import { StatsCard } from '@/components/dashboard/StatsCard'
import { DashboardSectionHeader } from '@/components/dashboard/DashboardSectionHeader'
import { MetricChart } from '@/components/dashboard/MetricChart'
import { useProtocolRevenue } from '@/hooks/useProtocolRevenue'
import { useTreasuryData } from '@/hooks/useTreasuryData'
import { useDashboardTimeSeries } from '@/hooks/useDashboardTimeSeries'
import { CHART_BLUE } from '@/lib/chartColors'
import type { TimePeriod } from '@/types/dashboard'

function RealtimeBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-400 whitespace-nowrap">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
      Real-time
    </span>
  )
}

interface ProtocolRevenueSectionProps {
  period: TimePeriod
}

export function ProtocolRevenueSection({ period }: ProtocolRevenueSectionProps) {
  const { data: revenue, isLoading: revenueLoading } = useProtocolRevenue(period)
  const { data: treasury, isLoading: treasuryLoading } = useTreasuryData()
  const { data: timeSeries, isLoading: timeSeriesLoading } = useDashboardTimeSeries(period)

  const loading = revenueLoading || treasuryLoading

  // Compute cumulative revenue from time series
  const cumulativeRevenue = useMemo(() => {
    if (!timeSeries?.revenue) return []
    let running = 0
    return timeSeries.revenue.map((point) => {
      running += point.value
      return { ...point, value: running }
    })
  }, [timeSeries?.revenue])

  return (
    <div>
      <DashboardSectionHeader
        icon={DollarSign}
        title="Protocol Revenue"
      />

      {/* Fee Breakdown: all parties */}
      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4 mb-4">
        <StatsCard
          title="Total Protocol Fees"
          value={loading ? '...' : `${revenue?.totalProtocolFeesSOL.toFixed(4) || '0.0000'} SOL`}
          subtitle="All parties combined"
          icon={DollarSign}
          variant="hero"
          loading={loading}
        />
        <StatsCard
          title="dextoro Revenue"
          value={loading ? '...' : `${revenue?.totalSOL.toFixed(4) || '0.0000'} SOL`}
          subtitle={
            revenue
              ? `Trading ${revenue.tradingRevenueSOL.toFixed(2)} + Migration ${revenue.migrationRevenueSOL.toFixed(2)} + DAMM V2 ${revenue.dammV2RevenueSOL.toFixed(2)} + DTR LP ${revenue.lpFeesSOL.toFixed(2)}`
              : 'What we retain'
          }
          icon={Coins}
          loading={loading}
        />
        <StatsCard
          title="Creator Revenue"
          value={loading ? '...' : `${revenue?.creatorRevenueSOL.toFixed(4) || '0.0000'} SOL`}
          subtitle="Earned by creators"
          icon={Users}
          loading={loading}
        />
        <StatsCard
          title="Meteora Protocol Fees"
          value={loading ? '...' : `${revenue?.meteoraRevenueSOL.toFixed(4) || '0.0000'} SOL`}
          subtitle="Meteora's share"
          icon={Building2}
          loading={loading}
        />
      </div>

      {/* Revenue detail */}
      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-3">
        {period !== 'all' && (
          <StatsCard
            title="Revenue Growth Rate"
            value={loading ? '...' : `${revenue?.growthRatePercent.toFixed(1) || '0.0'}%`}
            change={revenue ? {
              value: Number(revenue.growthRatePercent.toFixed(1)),
              trend: revenue.growthRatePercent >= 0 ? 'up' : 'down',
              suffix: '%',
              label: `vs previous ${period}`,
              colorOverride: revenue.growthRatePercent >= 0 ? '#4ADE80' : '#F87171',
            } : undefined}
            icon={BarChart3}
            loading={loading}
          />
        )}
        <StatsCard
          title="Avg Revenue Per Token"
          value={loading ? '...' : `${revenue?.avgRevenuePerToken.toFixed(6) || '0.000000'} SOL`}
          subtitle="SOL per token (lifetime)"
          icon={Coins}
          loading={loading}
        />
        <StatsCard
          title="Treasury Balance"
          value={loading ? '...' : `${treasury?.treasuryBalanceSOL.toFixed(4) || '0.0000'} SOL`}
          icon={Landmark}
          loading={loading}
          badge={<RealtimeBadge />}
        />
        <StatsCard
          title="Claimable Protocol Fees"
          value={loading ? '...' : `${treasury?.claimableFeesSOL.toFixed(4) || '0.0000'} SOL`}
          icon={AlertTriangle}
          warning={treasury && treasury.poolsWithFees > 0 ? `${treasury.poolsWithFees} pools with claimable fees` : undefined}
          loading={loading}
          badge={<RealtimeBadge />}
        />
      </div>
      <div className="mt-4 flex flex-col lg:flex-row gap-3 sm:gap-4">
        <div className="flex-1">
          <MetricChart
            type="area"
            title={period === 'all' ? 'Protocol Revenue (all time)' : `Protocol Revenue (${period})`}
            data={timeSeries?.revenue || []}
            valueSuffix=" SOL"
            loading={timeSeriesLoading}
          />
        </div>
        <div className="flex-1">
          <MetricChart
            type="area"
            title="Cumulative Revenue"
            data={cumulativeRevenue}
            valueSuffix=" SOL"
            color={CHART_BLUE}
            loading={timeSeriesLoading}
          />
        </div>
      </div>
    </div>
  )
}
